/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useState } from 'react';

import { useSnackbar } from '../../../components/feedback/SnackbarProvider';
import applyTwoPhaseLayout from '../layout/layout-helpers/applyTwoPhaseLayout';
import { generateNodesAndEdgesV2 } from '../utils/generateNodesAndEdges';

// Default spacing config matching what generateNodesAndEdgesV2 requires
const DEFAULT_SPACING_CONFIG = {
  horizontalScale: 2.2,
  verticalScale: 2.2,
  layerDistance: 250,
  nodeDistance: 120,
  edgeType: 'default',
  showActionFrames: true,
  layoutType: 'layered',
};

// Create a context for node generation and spacing controls
const NodeGenerationContext = createContext();

export const NodeGenerationProvider = ({ children }) => {
  // Use a single config object instead of individual state variables
  const [spacingConfig, setSpacingConfig] = useState({ ...DEFAULT_SPACING_CONFIG });
  const { showSnackbar } = useSnackbar();
  // Function to update a single property in the config
  const updateSpacingConfig = useCallback((key, value) => {
    setSpacingConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Convenience setters for individual properties
  const setHorizontalScale = useCallback(
    (value) => updateSpacingConfig('horizontalScale', value),
    [updateSpacingConfig]
  );
  const setVerticalScale = useCallback(
    (value) => updateSpacingConfig('verticalScale', value),
    [updateSpacingConfig]
  );
  const setLayerDistance = useCallback(
    (value) => updateSpacingConfig('layerDistance', value),
    [updateSpacingConfig]
  );
  const setNodeDistance = useCallback(
    (value) => updateSpacingConfig('nodeDistance', value),
    [updateSpacingConfig]
  );
  const setEdgeType = useCallback(
    (value) => updateSpacingConfig('edgeType', value),
    [updateSpacingConfig]
  );

  // Function to reset the config to defaults
  const resetSpacingConfig = useCallback(() => {
    setSpacingConfig({ ...DEFAULT_SPACING_CONFIG });
  }, []);

  // Function to generate nodes and edges from a recipe
  const generateNodesAndEdges = useCallback(
    (recipe) => generateNodesAndEdgesV2(recipe, spacingConfig, showSnackbar),
    [spacingConfig, showSnackbar]
  );

  // Function to position nodes with the current spacing settings
  const applyNodesPositioning = useCallback(
    (nodes, edges, _customConfig = {}) => {
      // Apply layout with the current config, overridden by any custom values
      const layoutOptions = {
        ...spacingConfig,
        // ...customConfig,
      };

      // Use applyTwoPhaseLayout which is what generateNodesAndEdgesV2 uses
      const layoutedNodes = applyTwoPhaseLayout(nodes, edges, layoutOptions);

      return Promise.resolve({
        nodes: layoutedNodes,
        edges: edges.map((edge) => ({
          ...edge,
          type: layoutOptions.edgeType,
        })),
      });
    },
    [spacingConfig]
  );

  // Create a context value with all the methods and state
  const contextValue = {
    // Unified spacing config
    spacingConfig,
    setSpacingConfig,
    updateSpacingConfig,
    resetSpacingConfig,

    // Individual property getters
    horizontalScale: spacingConfig.horizontalScale,
    verticalScale: spacingConfig.verticalScale,
    layerDistance: spacingConfig.layerDistance,
    nodeDistance: spacingConfig.nodeDistance,
    edgeType: spacingConfig.edgeType,

    // Individual property setters
    setHorizontalScale,
    setVerticalScale,
    setLayerDistance,
    setNodeDistance,
    setEdgeType,

    // Node generation and positioning
    generateNodesAndEdges,
    applyNodesPositioning,
  };

  return (
    <NodeGenerationContext.Provider value={contextValue}>{children}</NodeGenerationContext.Provider>
  );
};

// Custom hook to use the node generation context
export function useNodeGeneration() {
  const context = useContext(NodeGenerationContext);
  if (!context) {
    throw new Error('useNodeGeneration must be used within a NodeGenerationProvider');
  }
  return context;
}

// Export the default config for reference
export { DEFAULT_SPACING_CONFIG };
